import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, makeStyles, Typography, MenuItem, Backdrop } from '@material-ui/core';
import { t, Trans } from '@lingui/macro';
import { FormProvider, useForm } from 'react-hook-form';
import _, { get, isObject } from 'lodash';
import clsx from 'clsx';

import { Cancel, CheckCircleOutline, CloudDownload } from '@material-ui/icons';
import useSession from '../../../stores/SessionStore';
import { Actions } from '../../page-framework';
import { buildPageId } from '../AdminLinkUtils';
import AdminPage from '../AdminPage';
import { AdminPages } from '../AdminPages';
import { isEmailValid, USE_FORM_DEFAULT_OPTIONS, useFieldWatch } from '../../../utilities/FormUtilities';
import NgpForm from '../../form/NgpForm';
import { useConfigBreadcrumbs, useConfigurationData, useSaveConfigurationData } from './GeneralUtilities';
import { useGeoJsonExporter } from './GeneralAdminPageHelper';
import FormSection from '../../form/FormSection';
import NgpToggle from '../../form/NgpToggle';
import { FIELD_MAPPINGS, META_FIELD_MAPPINGS } from './Constants';
import NgpNumberField from '../../form/NgpNumberField';
import NgpTextField from '../../form/NgpTextField';
import { RowFlexLayout } from '../../layout';
import { useFormSubmitWrapper } from '../../../services/FormSaveService';
import NgpFileUpload from '../../form/NgpFileUpload';
import { validateGeoJson } from '../../../utilities/GeoJsonUtilities';
import useSmtpVerify from './UseSmtpVerify';
import NgpFieldHelpTip from '../../form/NgpFieldHelpTip';
import NgpEmailField from '../../form/NgpEmailField';
import { useSystemUpgradeDialog } from './SystemUpgradeDialog';
import NgpPasswordField from '../../form/NgpPasswordField';
import NgpFormButton from '../../form/NgpFormButton';
import { useIsGlobal, useIsSelf } from '../../../utilities/PgmHooks';
import useEnvironment from '../../../stores/EnvironmentStore';
import useNg1PairingVerify from './UseNg1PairingVerify';
import { useLicense } from '../license/LicenseHelper';
import NgpSelect from '../../form/NgpSelect';
import useAlerts from '../../../stores/AlertsStore';
import { useDataService } from '../../../services/ApiDataService';
import useInterval from '../../../utilities/UseInterval';
import { isBlank } from '../../properties/funcs';

const functionName = AdminPages.General;
const FIELD_WIDTH = 45;
const DEFAULT_INPUT_PROPS = {
    size: FIELD_WIDTH,
};
const excludeFromDirtyCheck = [META_FIELD_MAPPINGS.SMTP_TEST_EMAIL];

const MAX_LOADING_INTERVAL = 120000;
const MIGRATED_SERVER_ID_URL = '/ipm/ng1/migratedServerId';
const MIGRATED_SERVER_ID_POLL_INTERVAL = 5000;

const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 100,
        opacity: 0.7,
        color: '#fff',
    },
}));

export default function GeneralAdminPage() {
    const classes = useStyles();
    const [, /* Not used */ sessionActions] = useSession();
    const [environment, environmentActions] = useEnvironment();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, handleSubmit, setError } = methods;

    const breadcrumbs = useConfigBreadcrumbs();
    const { data, isLoading, error, reset: resetData } = useConfigurationData();

    const { submitWrapper } = useFormSubmitWrapper();
    const { save, isLoading: isSaving } = useSaveConfigurationData(data, resetData, setError);

    const _onSubmit = useMemo(() => submitWrapper(save), [submitWrapper, save]);
    const { dialog: upgradeDialog, show: showUpgradeDialog } = useSystemUpgradeDialog();

    const isSelf = useIsSelf();

    const dataService = useDataService();
    const ng1UrlRef = useRef();
    const [, /* Not used */ alertsActions] = useAlerts();
    const activeAssignedServerId = get(data, FIELD_MAPPINGS.NG1_ASSIGNED_SERVER_ID);
    const [ng1PairChanged, setNg1PairChanged] = useState(false);
    const [ng1PairVerified, setNg1PairVerified] = useState(true);
    const [migrationLoading, setMigrationLoading] = useState(false);
    const [ng1PairingVerifyLoading, setNg1PairingVerifyLoading] = useState(false);
    const [migrationLoadingStart, setMigrationLoadingStart] = useState(0);

    const onNg1PairingChange = useCallback((ng1PairChange) => {
        setNg1PairChanged(ng1PairChange);
    }, []);

    const onNg1PairingVerify = useCallback(
        (verified) => {
            if (verified) {
                alertsActions.handleRemove();
            }
            setNg1PairVerified(verified);
        },
        [alertsActions]
    );

    const onNg1PairingVerifyLoading = useCallback((isVerifyLoading) => {
        setNg1PairingVerifyLoading(isVerifyLoading);
    }, []);

    const handleFormSubmit = (submitData) => {
        if (ng1PairChanged && !ng1PairVerified) {
            ng1UrlRef.current.focus();
            alertsActions.createWarningAlert(t`nGeniusONE Pairing has changed, please click VERIFY button.`);
            return;
        }

        if (ng1PairChanged) {
            handleCheckMigrationStatus();
        }
        onSubmit(submitData);
    };

    // Periodically poll the migratedServerId whenever the user has saving the nGeniusONE pairing, until
    // the migratedServerId is equal to the activeAssignedServerId (or more than 2 minutes has elapsed).
    const checkMigrationStatus = async () => {
        if (migrationLoading) {
            if (Date.now() - migrationLoadingStart > MAX_LOADING_INTERVAL) {
                setMigrationLoading(false);
                setNg1PairChanged(false);
                return;
            }
            try {
                const response = await dataService.get(MIGRATED_SERVER_ID_URL);
                if (response === activeAssignedServerId) {
                    setMigrationLoading(false);
                    setNg1PairChanged(false);
                }
            } catch (e) {
                setMigrationLoading(false);
                setNg1PairChanged(false);
                if (e?.responseBody?.error) {
                    alertsActions.createErrorAlert(e.responseBody.error);
                }
            }
        }
    };

    // Start check migration status on the page
    const handleCheckMigrationStatus = () => {
        if (ng1PairChanged && ng1PairVerified) {
            setMigrationLoading(true);
            setMigrationLoadingStart(Date.now());
        }
    };

    useInterval(() => checkMigrationStatus(), MIGRATED_SERVER_ID_POLL_INTERVAL);

    const onSubmit = useCallback(
        async (submitData) => {
            const { sysConfig, meta } = submitData;
            if (!meta.smtpEnabled) sysConfig.smtp = null;

            return _onSubmit(submitData)
                .then(() => environmentActions.load())
                .then(() => sessionActions.refresh(environment));
        },
        [_onSubmit, sessionActions, environment, environmentActions]
    );

    // Keep the form updated with the latest data
    useEffect(() => {
        reset(data || {});
    }, [data, reset]);

    return (
        <>
            <Backdrop className={classes.backdrop} open={migrationLoading || ng1PairingVerifyLoading}>
                <CircularProgress color="inherit" />
                <Typography variant="h6" className={classes.textContainer}>
                    {migrationLoading
                        ? t`Applying nGeniusONE Pairing configuration, please wait...`
                        : t`Verifying nGeniusONE Pairing configuration, please wait...`}
                </Typography>
            </Backdrop>
            {upgradeDialog}
            <AdminPage
                breadcrumbs={breadcrumbs}
                pageId={buildPageId(functionName)}
                actions={
                    <Actions>
                        {isSelf && (
                            <Button color="primary" variant="contained" onClick={showUpgradeDialog}>
                                <Trans>Upgrade System</Trans>
                            </Button>
                        )}
                        <Button color="primary" variant="contained" onClick={handleSubmit(handleFormSubmit)}>
                            <Trans>Save</Trans>
                        </Button>
                    </Actions>
                }
            >
                <FormProvider {...methods}>
                    <GeneralAdminForm
                        data={data}
                        error={error}
                        isLoading={isLoading}
                        isSaving={isSaving}
                        submitFunction={handleFormSubmit}
                        ng1UrlRef={ng1UrlRef}
                        onNg1PairingChange={onNg1PairingChange}
                        onNg1PairingVerify={onNg1PairingVerify}
                        onNg1PairingVerifyLoading={onNg1PairingVerifyLoading}
                    />
                </FormProvider>
            </AdminPage>
        </>
    );
}

function GeneralAdminForm({
    data,
    isLoading,
    isSaving,
    error,
    submitFunction,
    ng1UrlRef,
    onNg1PairingChange,
    onNg1PairingVerify,
    onNg1PairingVerifyLoading,
}) {
    const { isLoading: isLoadingLicense, license, getLicense } = useLicense();
    const isGlobal = useIsGlobal();

    const isInitialized = !error && !!data && !isLoading && !isLoadingLicense;

    // We don't want to show IPM in we're in nGeniusEDGE, however, we do want to show
    // it if we're an nGeniusEDGE with a real server license, not the fake one that
    // we create for nGE-only boxes. This allows us to provision an nGE into an existing
    // nGP deployment.
    const isServerLicenseArtificial = get(license, 'server.artificial', false);
    const isSingleSem = get(data, 'sysConfig.singleSEM', false);
    const showIpmSection = !isSingleSem || (isSingleSem && !isServerLicenseArtificial);

    useEffect(() => {
        getLicense();
    }, [getLicense]);

    if (error && !isLoading && !isLoadingLicense) {
        return (
            <div>
                <Trans>An error occurred loading the data for this form</Trans>
            </div>
        );
    }

    return (
        <NgpForm
            initialized={isInitialized}
            loading={isLoading || isSaving}
            submitFunction={submitFunction}
            excludeFromDirtyCheck={excludeFromDirtyCheck}
        >
            <UserPasswordRulesSection />
            <WebSessionManagmentSection />
            <SmtpSection />
            <SnmpEngineIdSection />
            <NgpUrlSection />
            {!isGlobal && (
                <>
                    {showIpmSection && <IpmSection />}
                    <NotificationSection />
                </>
            )}
            <SiteMapSection data={data} />
            <Ng1PairingSection
                data={data}
                onNg1PairingChange={onNg1PairingChange}
                onNg1PairingVerify={onNg1PairingVerify}
                onNg1PairingVerifyLoading={onNg1PairingVerifyLoading}
                ng1UrlRef={ng1UrlRef}
            />
        </NgpForm>
    );
}

function UserPasswordRulesSection() {
    return (
        <FormSection title={<Trans>User Password Rules</Trans>}>
            <NgpToggle
                fieldName={FIELD_MAPPINGS.VALIDATE_PASSWORD_COMPLEXITY}
                label={<Trans>Require Password Complexity</Trans>}
                helpTipText={
                    <Trans>Turning this on will add password complexity rules to include numbers and mixed case</Trans>
                }
            />
            <NgpNumberField
                fieldName={FIELD_MAPPINGS.PASSWORD_MIN_LENGTH}
                label={<Trans>Minimum Password Length</Trans>}
                helpTipText={<Trans>Specify a number from 8 to 20</Trans>}
                min={8}
                max={20}
                required
                inputProps={DEFAULT_INPUT_PROPS}
            />
        </FormSection>
    );
}

function WebSessionManagmentSection() {
    const enforceSessionTimeouts = useFieldWatch(FIELD_MAPPINGS.SYSTEM_TIMEOUT);
    const disableUserAccountAfterInactivity = useFieldWatch(FIELD_MAPPINGS.CHECK_USER_INACTIVITY);

    return (
        <FormSection title={<Trans>Web Session Management</Trans>}>
            <NgpNumberField
                fieldName={FIELD_MAPPINGS.NUM_LOGIN_ATTEMPTS_TO_LOCK_OUT}
                label={<Trans>Account Lockout Threshold</Trans>}
                helpTipText={
                    <Trans>Number of failed login attempts before user lockout. Specify a number from 1 to 20</Trans>
                }
                min={1}
                max={20}
                required
                inputProps={DEFAULT_INPUT_PROPS}
            />
            <NgpNumberField
                fieldName={FIELD_MAPPINGS.LOCK_OUT_TIME_MINUTES}
                label={<Trans>Lockout Time in Minutes</Trans>}
                helpTipText={<Trans>Specify a number from 3 to 14400 (10 days)</Trans>}
                min={3}
                max={14400}
                required
                inputProps={DEFAULT_INPUT_PROPS}
            />

            <NgpToggle fieldName={FIELD_MAPPINGS.SYSTEM_TIMEOUT} label={<Trans>Enforce Session Timeouts</Trans>} />
            {enforceSessionTimeouts && (
                <NgpNumberField
                    fieldName={FIELD_MAPPINGS.SYSTEM_TIMEOUT_MINUTES}
                    label={<Trans>Session Timeout in Minutes</Trans>}
                    helpTipText={<Trans>Specify a number from 10 to 99</Trans>}
                    min={10}
                    max={99}
                    required
                    inputProps={DEFAULT_INPUT_PROPS}
                />
            )}

            <NgpToggle
                fieldName={FIELD_MAPPINGS.CHECK_USER_INACTIVITY}
                label={<Trans>Disable User Account After Inactivity</Trans>}
            />
            {disableUserAccountAfterInactivity && (
                <NgpNumberField
                    fieldName={FIELD_MAPPINGS.DISABLE_AFTER_DAYS_INACTIVE}
                    label={<Trans>Days of inactivity before disabling user</Trans>}
                    helpTipText={<Trans>Specify a number of days from 30 to 365 (1 year)</Trans>}
                    min={30}
                    max={365}
                    required
                    inputProps={DEFAULT_INPUT_PROPS}
                />
            )}
        </FormSection>
    );
}

const useSmtpSectionStyles = makeStyles((theme) => ({
    validator: {
        padding: theme.spacing(0.5, 0),
        ...theme.typography.subtitle2,

        '&$success .MuiSvgIcon-root': {
            color: theme.palette.success.main,
        },

        '&$failure .MuiSvgIcon-root': {
            color: theme.palette.error.main,
        },
    },

    success: {},
    failure: {},
}));

function SmtpSection() {
    const classes = useSmtpSectionStyles();

    const smtpEnabled = useFieldWatch(META_FIELD_MAPPINGS.SMTP_ENABLED);
    const username = useFieldWatch(FIELD_MAPPINGS.SMTP_USERNAME);
    const password = useFieldWatch(FIELD_MAPPINGS.SMTP_PASSWORD);
    const security = useFieldWatch(FIELD_MAPPINGS.SMTP_SECURITY);
    const enableSmtpServerCertVerification = !useFieldWatch(FIELD_MAPPINGS.SMTP_NO_CERTS);
    const host = useFieldWatch(FIELD_MAPPINGS.SMTP_SERVER);
    const port = useFieldWatch(FIELD_MAPPINGS.SMTP_PORT);
    const from = useFieldWatch(FIELD_MAPPINGS.SMTP_SEND_EMAIL);
    const to = useFieldWatch(META_FIELD_MAPPINGS.SMTP_TEST_EMAIL);
    const headerName = useFieldWatch(FIELD_MAPPINGS.SMTP_HEADER_NAME);
    const headerValue = useFieldWatch(FIELD_MAPPINGS.SMTP_HEADER_VALUE);

    const canValidate = to && from && host && port && security && isEmailValid(to) && isEmailValid(from);

    const { testEmail, isLoading, smtpSuccess, smtpMessage, smtpResponse, hasTested } = useSmtpVerify();

    const inputProps = useMemo(
        () => ({
            [FIELD_MAPPINGS.SMTP_USERNAME]: {
                ...DEFAULT_INPUT_PROPS,
                maxLength: 40,
            },
            [FIELD_MAPPINGS.SMTP_PASSWORD]: {
                ...DEFAULT_INPUT_PROPS,
                maxLength: 4096,
            },
            [FIELD_MAPPINGS.SMTP_SERVER]: {
                ...DEFAULT_INPUT_PROPS,
                maxLength: 40,
            },
            [FIELD_MAPPINGS.SMTP_PORT]: {
                size: Math.round(FIELD_WIDTH / 4),
                maxLength: 5,
            },
            [FIELD_MAPPINGS.SMTP_SEND_EMAIL]: {
                ...DEFAULT_INPUT_PROPS,
                maxLength: 100,
            },
            [FIELD_MAPPINGS.SMTP_HEADER_NAME]: {
                ...DEFAULT_INPUT_PROPS,
                maxLength: 40,
            },
            [FIELD_MAPPINGS.SMTP_HEADER_VALUE]: {
                ...DEFAULT_INPUT_PROPS,
                maxLength: 40,
            },
        }),
        []
    );

    const doVerify = useCallback(() => {
        testEmail({
            security: security || 'TLS', // Defaults to TLS
            disableServerCertVerification: !enableSmtpServerCertVerification, // avoiding a null value
            username,
            password,
            host,
            port,
            from,
            to,
            headerName,
            headerValue,
        });
    }, [
        testEmail,
        security,
        username,
        password,
        host,
        port,
        from,
        to,
        enableSmtpServerCertVerification,
        headerName,
        headerValue,
    ]);

    const smtpHeaderValidationRules = useMemo(() => {
        const validateSmtpHeaderName = (value) => {
            if (!value) return true;

            // RFC 5322: field name must be US-ASCII char (33-126), excluding (:)
            const validFieldNameRegex = /^[\x21-\x39\x3B-\x7E]+$/;
            if (!validFieldNameRegex.test(value)) {
                return 'Header name must contain only printable ASCII characters (excluding colon)';
            }

            const reservedHeaders = [
                'date',
                'from',
                'sender',
                'reply-to',
                'to',
                'cc',
                'bcc',
                'message-id',
                'in-reply-to',
                'references',
                'subject',
                'comments',
                'keywords',
                'return-path',
                'received',
            ];
            if (reservedHeaders.includes(value.toLowerCase())) {
                return 'Cannot use reserved header names';
            }

            if (value.length > 40) {
                return 'Header name too long (max 40 characters)';
            }

            return true;
        };

        const validateSmtpHeaderValue = (value) => {
            if (!value) return true;

            // RFC 5322: field body may include ASCII + SP(32) + HTAB(9)
            // eslint-disable-next-line no-control-regex
            const validFieldValueRegex = /^[\x20\x09\x21-\x7E]*$/;
            if (!validFieldValueRegex.test(value)) {
                return 'Header value contains invalid characters (only printable ASCII, space, and tab allowed)';
            }

            // Check for leading/trailing whitespace
            if (/^\s|\s$/.test(value)) {
                return 'Header value should not start or end with whitespace';
            }

            if (value.length > 998) {
                return 'Header value too long (max 998 characters per RFC 5322)';
            }

            return true;
        };

        return {
            headerName: {
                validate: {
                    headerName: validateSmtpHeaderName,
                },
            },
            headerValue: {
                validate: {
                    headerValue: validateSmtpHeaderValue,
                },
            },
        };
    }, []);

    return (
        <FormSection title={<Trans>SMTP</Trans>}>
            <NgpToggle fieldName={META_FIELD_MAPPINGS.SMTP_ENABLED} label={<Trans>Use SMTP for System Emails</Trans>} />
            {smtpEnabled && (
                <>
                    <NgpTextField
                        fieldName={FIELD_MAPPINGS.SMTP_USERNAME}
                        label={<Trans>Username</Trans>}
                        inputProps={inputProps[FIELD_MAPPINGS.SMTP_USERNAME]}
                    />
                    <NgpPasswordField
                        fieldName={FIELD_MAPPINGS.SMTP_PASSWORD}
                        label={<Trans>Password</Trans>}
                        togglePasswordVisibility
                        inputProps={inputProps[FIELD_MAPPINGS.SMTP_PASSWORD]}
                    />
                    <RowFlexLayout>
                        <NgpTextField
                            fieldName={FIELD_MAPPINGS.SMTP_SERVER}
                            label={<Trans>Hostname/IP</Trans>}
                            required
                            inputProps={inputProps[FIELD_MAPPINGS.SMTP_SERVER]}
                        />
                        <Box pl={1}>
                            <NgpNumberField
                                fieldName={FIELD_MAPPINGS.SMTP_PORT}
                                label={<Trans>Port</Trans>}
                                required
                                inputProps={inputProps[FIELD_MAPPINGS.SMTP_PORT]}
                            />
                        </Box>
                    </RowFlexLayout>
                    <NgpEmailField
                        fieldName={FIELD_MAPPINGS.SMTP_SEND_EMAIL}
                        required
                        label={<Trans>From Address</Trans>}
                        inputProps={inputProps[FIELD_MAPPINGS.SMTP_SEND_EMAIL]}
                    />
                    <NgpSelect fieldName={FIELD_MAPPINGS.SMTP_SECURITY} required label={<Trans>Security</Trans>}>
                        <MenuItem key="None" value="None">
                            None
                        </MenuItem>
                        <MenuItem key="STARTTLS" value="STARTTLS">
                            STARTTLS
                        </MenuItem>
                        <MenuItem key="TLS" value="TLS">
                            TLS
                        </MenuItem>
                    </NgpSelect>
                    {security !== 'None' && (
                        <NgpToggle
                            fieldName={FIELD_MAPPINGS.SMTP_NO_CERTS}
                            label={<Trans>Verify SMTP Server Certificate</Trans>}
                            inverted
                        />
                    )}
                    {security !== 'None' && (
                        <RowFlexLayout>
                            <NgpTextField
                                fieldName={FIELD_MAPPINGS.SMTP_HEADER_NAME}
                                label={<Trans>Header Name</Trans>}
                                inputProps={inputProps[FIELD_MAPPINGS.SMTP_HEADER_NAME]}
                                fieldRules={smtpHeaderValidationRules.headerName}
                                helpTipText={
                                    <Trans>
                                        Custom header name for SMTP emails. Must contain only printable ASCII characters
                                        and cannot be a reserved header.
                                    </Trans>
                                }
                            />
                            <Box pl={1}>
                                <NgpTextField
                                    fieldName={FIELD_MAPPINGS.SMTP_HEADER_VALUE}
                                    label={<Trans>Header Value</Trans>}
                                    inputProps={inputProps[FIELD_MAPPINGS.SMTP_HEADER_VALUE]}
                                    fieldRules={smtpHeaderValidationRules.headerValue}
                                    helpTipText={
                                        <Trans>
                                            Custom header value for SMTP emails. Cannot contain line breaks or control
                                            characters.
                                        </Trans>
                                    }
                                />
                            </Box>
                        </RowFlexLayout>
                    )}
                    <RowFlexLayout vTop>
                        <NgpEmailField
                            fieldName={META_FIELD_MAPPINGS.SMTP_TEST_EMAIL}
                            label={<Trans>Test Email Address</Trans>}
                            inputProps={{
                                size: FIELD_WIDTH,
                                maxLength: 40,
                            }}
                        />
                        <RowFlexLayout pl={1} mt={5} vCenter>
                            <NgpFormButton
                                size="small"
                                onClick={doVerify}
                                disabled={isLoading || !canValidate}
                                startIcon={
                                    isLoading ? (
                                        <CircularProgress variant="indeterminate" size={14} />
                                    ) : (
                                        <CheckCircleOutline />
                                    )
                                }
                            >
                                {isLoading ? <Trans>Verifying...</Trans> : <Trans>Verify</Trans>}
                            </NgpFormButton>
                            <NgpFieldHelpTip
                                isToggleField
                                ml={1}
                                helpText={
                                    <Trans>
                                        This allows you to verify your SMTP configuration by sending an email to the
                                        provided address
                                    </Trans>
                                }
                            />
                        </RowFlexLayout>
                    </RowFlexLayout>
                    {hasTested && !isLoading && smtpResponse && (
                        <RowFlexLayout
                            vCenter
                            mt={1}
                            className={clsx(classes.validator, {
                                [classes.success]: smtpSuccess,
                                [classes.failure]: !smtpSuccess,
                            })}
                        >
                            {smtpSuccess ? <CheckCircleOutline color="inherit" /> : <Cancel color="inherit" />}
                            <Box ml={1}>
                                {smtpSuccess ? (
                                    <Trans>SMTP send successful!</Trans>
                                ) : (
                                    <Trans>{smtpMessage}</Trans> || <Trans>Failed to send test email</Trans>
                                )}
                            </Box>
                        </RowFlexLayout>
                    )}
                </>
            )}
        </FormSection>
    );
}

function SnmpEngineIdSection() {
    const snmpEngineIdRules = useMemo(
        () => ({
            validate: {
                hexLength: (value) => {
                    const trimmed = _.trim(value || '');
                    if (_.isEmpty(trimmed)) {
                        return 'Value is required';
                    }
                    if (trimmed.length < 10 || trimmed.length > 64) {
                        return 'SNMP Engine ID must be between 10 and 64 characters';
                    }
                    if (trimmed.length % 2 !== 0) {
                        return 'SNMP Engine ID must contain an even number of characters';
                    }
                    if (!/^[0-9a-fA-F]+$/.test(trimmed)) {
                        return 'SNMP Engine ID must be hexadecimal';
                    }
                    return true;
                },
            },
        }),
        []
    );

    return (
        <FormSection title={<Trans>SNMPv3 Engine ID</Trans>}>
            <NgpTextField
                fieldName={FIELD_MAPPINGS.SNMP_ENGINE_ID}
                label={<Trans>SNMPv3 Engine ID</Trans>}
                required
                fieldRules={snmpEngineIdRules}
                helpTipText={
                    <Trans>
                        The SNMP Engine ID uniquely identifies the SNMP entity. Example: 8000008d8000000000000000. This
                        is used for SNMPv3 authentication and privacy.
                    </Trans>
                }
                inputProps={{ ...DEFAULT_INPUT_PROPS, maxLength: 64 }}
            />
        </FormSection>
    );
}

function NgpUrlSection() {
    return (
        <FormSection title={<Trans>nGeniusPULSE URL</Trans>}>
            <NgpTextField
                fieldName={FIELD_MAPPINGS.PORTAL_LOCATION}
                label={<Trans>nGeniusPULSE URL</Trans>}
                required
                helpTipText={
                    <Trans>
                        The URL to nGeniusPULSE. An example would be https://localhost. This is set so that links in
                        emails go to the right place.
                    </Trans>
                }
                inputProps={DEFAULT_INPUT_PROPS}
            />
        </FormSection>
    );
}

function IpmSection() {
    return (
        <FormSection title={<Trans>Infrastructure Monitoring</Trans>}>
            <NgpToggle
                fieldName={FIELD_MAPPINGS.DISABLE_IPM}
                label={<Trans>Disable Infrastructure Monitoring</Trans>}
            />
        </FormSection>
    );
}

function NotificationSection() {
    return (
        <FormSection title={<Trans>Notifications</Trans>}>
            <NgpToggle
                fieldName={FIELD_MAPPINGS.MERIDIEM_TIME_FORMAT}
                inverted
                label={<Trans>24 hour time format for emails</Trans>}
            />
        </FormSection>
    );
}

const useSiteMapStyles = makeStyles((theme) => ({
    pristine: {
        color: theme.palette.primary.main,
    },

    error: {
        color: theme.palette.error.main,
    },

    success: {
        color: theme.palette.success.main,
    },
}));

function SiteMapSection({ data }) {
    const classes = useSiteMapStyles();

    const { exportGeoJson, isLoading: isExporting } = useGeoJsonExporter();
    const activeFilename = get(data, META_FIELD_MAPPINGS.GEOJSON_FILENAME);
    const currentFilename = useFieldWatch(FIELD_MAPPINGS.GEOJSON_FILENAME);
    const hasGeoJson = !!useFieldWatch(FIELD_MAPPINGS.GEOJSON_FILE);

    const fileUploadRules = useMemo(
        () => ({
            validate: {
                json: ensureValueIsJsonObject,
                geojson: validateGeoJson,
            },
        }),
        []
    );

    const buttonProps = useMemo(
        () => ({
            disabled: isExporting,
        }),
        [isExporting]
    );

    return (
        <FormSection title={<Trans>Sites Overview Map Overlay</Trans>}>
            <RowFlexLayout vTop mt={1}>
                <NgpFileUpload
                    fieldName={FIELD_MAPPINGS.GEOJSON_FILE}
                    fileNameFieldName={FIELD_MAPPINGS.GEOJSON_FILENAME}
                    label="Upload GeoJSON"
                    maxFileSize={10e6 * 5}
                    convertValue={(v) => JSON.parse(v)}
                    rules={fileUploadRules}
                    buttonProps={buttonProps}
                />

                {!!activeFilename && (
                    <Box ml={1} className={classes.pristine}>
                        <NgpFormButton
                            component="span"
                            size="small"
                            disabled={isExporting}
                            onClick={() => exportGeoJson(activeFilename)}
                            startIcon={
                                isExporting ? (
                                    <CircularProgress size={16} variant="indeterminate" color="primary" />
                                ) : (
                                    <CloudDownload />
                                )
                            }
                        >
                            <Trans>Export GeoJSON</Trans>
                        </NgpFormButton>
                    </Box>
                )}
            </RowFlexLayout>

            <NgpToggle
                fieldName={FIELD_MAPPINGS.ALLOW_GEOJSON_OVERLAY}
                disabled={!hasGeoJson}
                label={
                    <>
                        <Trans>Enable GeoJSON Map Overlay</Trans>
                        <span>&nbsp;</span>
                        <Typography variant="caption">
                            {hasGeoJson && !!(currentFilename || activeFilename) ? (
                                `(${currentFilename || activeFilename})`
                            ) : (
                                <Trans>(No GeoJSON file uploaded)</Trans>
                            )}
                        </Typography>
                    </>
                }
            />
        </FormSection>
    );
}

function Ng1PairingSection({ data, ng1UrlRef, onNg1PairingChange, onNg1PairingVerify, onNg1PairingVerifyLoading }) {
    const classes = useSmtpSectionStyles();

    const activeNg1Url = get(data, FIELD_MAPPINGS.NG1_URL);
    const activePreSharedKey = get(data, FIELD_MAPPINGS.NG1_PRE_SHARED_KEY);
    const activeCertCheck = get(data, FIELD_MAPPINGS.NG1_DISABLE_CERT_CHECK);
    const activeAssignedServerId = get(data, FIELD_MAPPINGS.NG1_ASSIGNED_SERVER_ID);

    const ng1Url = useFieldWatch(FIELD_MAPPINGS.NG1_URL);
    const preSharedKey = useFieldWatch(FIELD_MAPPINGS.NG1_PRE_SHARED_KEY);
    const certCheck = useFieldWatch(FIELD_MAPPINGS.NG1_DISABLE_CERT_CHECK); // certCheck is an inverted value; when set to false that means certs WILL be checked
    const assignedServerId = useFieldWatch(FIELD_MAPPINGS.NG1_ASSIGNED_SERVER_ID); // certCheck is an inverted value; when set to false that means certs WILL be checked

    const { testNg1Server, isLoading, nG1PairingSuccess, hasTested } = useNg1PairingVerify();

    const inputProps = useMemo(
        () => ({
            ...DEFAULT_INPUT_PROPS,
            maxLength: 256,
        }),
        []
    );

    const isNg1PairingClear = useCallback(() => {
        return isBlank(ng1Url) && isBlank(preSharedKey) && isBlank(assignedServerId);
    }, [ng1Url, preSharedKey, assignedServerId]);

    const isNg1PairingChanged = useCallback(() => {
        return (
            ng1Url !== activeNg1Url ||
            preSharedKey !== (!activePreSharedKey ? null : activePreSharedKey) ||
            certCheck !== activeCertCheck ||
            (_.isNil(assignedServerId) || assignedServerId === '' ? null : parseFloat(assignedServerId)) !==
                (_.isNil(activeAssignedServerId) ? null : activeAssignedServerId)
        );
    }, [
        ng1Url,
        preSharedKey,
        certCheck,
        assignedServerId,
        activeNg1Url,
        activePreSharedKey,
        activeCertCheck,
        activeAssignedServerId,
    ]);

    const handleNg1PairingChange = useCallback(() => {
        if (isNg1PairingClear()) {
            onNg1PairingChange(false);
            onNg1PairingVerify(true);
            return;
        }

        if (isNg1PairingChanged()) {
            onNg1PairingChange(true);
            onNg1PairingVerify(false);
        } else {
            onNg1PairingChange(false);
            onNg1PairingVerify(true);
        }
    }, [isNg1PairingClear, isNg1PairingChanged, onNg1PairingChange, onNg1PairingVerify]);

    const doVerify = useCallback(() => {
        testNg1Server({
            ng1Url,
            preSharedKey,
            certCheck,
            assignedServerId,
        });
    }, [testNg1Server, ng1Url, preSharedKey, certCheck, assignedServerId]);

    useEffect(() => {
        onNg1PairingVerifyLoading(isLoading);
        if (!isLoading) {
            onNg1PairingVerify(!hasTested ? true : nG1PairingSuccess);
        }
    }, [onNg1PairingVerifyLoading, onNg1PairingVerify, isLoading, hasTested, nG1PairingSuccess]);

    useEffect(() => {
        handleNg1PairingChange();
    }, [handleNg1PairingChange, ng1Url, preSharedKey, certCheck, assignedServerId]);

    return (
        <FormSection title={<Trans>nGeniusONE Pairing</Trans>}>
            <NgpTextField
                fieldName={FIELD_MAPPINGS.NG1_URL}
                label={<Trans>nGeniusONE server URL</Trans>}
                inputProps={inputProps}
                inputRef={ng1UrlRef}
            />
            <NgpPasswordField
                fieldName={FIELD_MAPPINGS.NG1_PRE_SHARED_KEY}
                label={<Trans>nGeniusONE pre-shared key</Trans>}
                togglePasswordVisibility
                inputProps={inputProps}
            />
            <NgpNumberField
                fieldName={FIELD_MAPPINGS.NG1_ASSIGNED_SERVER_ID}
                label={<Trans>nGeniusONE assigned server id</Trans>}
                helpTipText={<Trans>nGeniusONE assigned server id from 0 to 255</Trans>}
                min={0}
                max={255}
                inputProps={DEFAULT_INPUT_PROPS}
            />
            <NgpToggle
                fieldName={FIELD_MAPPINGS.NG1_DISABLE_CERT_CHECK}
                label={<Trans>Verify nGeniusONE Server Certificate</Trans>}
                inverted
            />
            <RowFlexLayout pl={1} mt={5} vCenter>
                <NgpFormButton
                    size="small"
                    onClick={doVerify}
                    disabled={isLoading}
                    startIcon={
                        isLoading ? <CircularProgress variant="indeterminate" size={14} /> : <CheckCircleOutline />
                    }
                >
                    {isLoading ? <Trans>Verifying...</Trans> : <Trans>Verify</Trans>}
                </NgpFormButton>
                <NgpFieldHelpTip
                    isToggleField
                    ml={1}
                    helpText={
                        <Trans>
                            This allows you to verify your nGeniusONE configuration by connecting to the server on the
                            URL provided above
                        </Trans>
                    }
                />
            </RowFlexLayout>
            {hasTested && !isLoading && (
                <RowFlexLayout
                    vCenter
                    mt={1}
                    className={clsx(classes.validator, {
                        [classes.success]: nG1PairingSuccess,
                        [classes.failure]: !nG1PairingSuccess,
                    })}
                >
                    {nG1PairingSuccess ? <CheckCircleOutline color="inherit" /> : <Cancel color="inherit" />}
                    <Box ml={1}>
                        <Trans>
                            {nG1PairingSuccess ? 'Pairing successful!' : 'Failed to pair with the nGeniusONE server'}
                        </Trans>
                    </Box>
                </RowFlexLayout>
            )}
        </FormSection>
    );
}

function ensureValueIsJsonObject(value) {
    if (isObject(value)) return true;

    try {
        JSON.parse(value);
    } catch (e) {
        return String(e);
    }

    return true;
}
